// Which batch actions a lifecycle view offers — the Song Library's rules
// (src/pages/songs.tsx ~702-722) shared verbatim:
//
//   Active view   → Archive only (permanent delete is never reachable here)
//   Archived view → Restore + Delete
//   All view      → depends on whether the selection is homogeneous

import type { BatchActionValue, SelectionLifecycle } from '@/features/tracks/useBatchActions';
import type { CatalogLifecycleView } from './catalogLifecycleTypes';

export interface CatalogBatchOption {
  label: string;
  value: BatchActionValue;
}

const ARCHIVE_ONLY: CatalogBatchOption[] = [
  { label: 'Archive Selected', value: 'archive' },
];

const ARCHIVED_ACTIONS: CatalogBatchOption[] = [
  { label: 'Restore Selected', value: 'restore' },
  { label: 'Delete Selected', value: 'delete' },
];

export function catalogBatchOptions(
  view: CatalogLifecycleView,
  selectionLifecycle: SelectionLifecycle,
  /**
   * Owner-approved 2026-08-11: when a mixed All-tab selection provides its
   * status-subset counts, offer scoped actions (Song Library parity) instead
   * of disabling Batch Actions.
   */
  mixedCounts?: { active: number; archived: number },
): CatalogBatchOption[] {
  if (view === 'active') return ARCHIVE_ONLY;
  if (view === 'archived') return ARCHIVED_ACTIONS;
  if (selectionLifecycle === 'active') return ARCHIVE_ONLY;
  if (selectionLifecycle === 'archived') return ARCHIVED_ACTIONS;
  if (selectionLifecycle === 'mixed' && mixedCounts) {
    return [
      { label: `Archive Active (${mixedCounts.active})`, value: 'archive' },
      {
        label: `Restore Archived (${mixedCounts.archived})`,
        value: 'restore',
      },
      { label: `Delete Archived (${mixedCounts.archived})`, value: 'delete' },
    ];
  }
  return [];
}

/**
 * Mixed Active+Archived selections cannot map to one action. Same warning the
 * Song Library shows, with the entity noun swapped in.
 */
export function mixedSelectionWarning(nounPlural: string): string {
  return `Select only Active ${nounPlural} to Archive, or only Archived ${nounPlural} to Restore/Delete.`;
}
